import { useContext, useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { SmartContext } from '../../../library/Core/SmartContext';
import { GlobalContext } from '../../../library/Core/GlobalContext';
import SessionContext from '../../../library/Core/SessionContext';
import { allowPerformAction, displayActionNotAllowedAlert, getActionElementClass, isEmpty, setError } from '../../../library/Core/SmartFunctions';
import { getStudentImage } from '../../../common/styles/Images';
import BusinessDirectoryService from './BusinessDirectoryService';
import useSmartState from '../../../common/hooks/useSmartState';
import FormBuilder from '../../../library/Builders/FormBuilder';
import AddProviderPageConfig from './PageConfigAddProvider.json';
import { toast } from 'react-toastify';

function AddServiceProviderModal(props) {
    const { state, dispatch } = useContext(SmartContext);
    const { globalState } = useContext(GlobalContext);
    const { sessionState } = useContext(SessionContext);
    const { setStateValue } = useSmartState();

    // Edit mode / Approve mode props
    const { editData, approveData, onEditClose } = props;
    const isApproveMode = !!approveData && !editData;
    const isEditMode = !!editData && !isApproveMode;
    // Both modes share the same prefill mechanism
    const prefillSource = editData || approveData;
    const isPrefillMode = !!prefillSource;
    const isSuperAdmin = sessionState?.userTypeCode === 'SUPER_ADMIN';
    const isAlumni = sessionState?.userTypeCode === 'ALUMNI' || sessionState?.userTypeCode === 'STUDENT';

    const [show, setShow] = useState(false);

    // Fields NOT handled by FormBuilder
    const [selectedInstituteId, setSelectedInstituteId] = useState('');
    const [searchText, setSearchText] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedAlumni, setSelectedAlumni] = useState(null);
    const [availabilityStatus, setAvailabilityStatus] = useState('');
    const [consentConfirmed, setConsentConfirmed] = useState(false);
    const [additionalNotes, setAdditionalNotes] = useState('');

    // Institute domain for SUPER_ADMIN
    const instituteDomain = globalState?.domain?.get('INSTITUTE_DOMAIN') || [];
    const [instituteSearchText, setInstituteSearchText] = useState('');
    const [showInstituteDropdown, setShowInstituteDropdown] = useState(false);

    const filteredInstitutes = useMemo(() => {
        if (!instituteSearchText || instituteSearchText.length < 2) return [];
        const term = instituteSearchText.toLowerCase();
        return instituteDomain.filter((item) => item.value?.toLowerCase().includes(term)).slice(0, 20);
    }, [instituteSearchText, instituteDomain]);

    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [showSearchDropdown, setShowSearchDropdown] = useState(false);

    const debounceRef = useRef(null);

    const resolvedInstituteId = useMemo(() => {
        // Case 1 — Edit: institute is fixed from the existing provider record
        if (isEditMode) {
            return selectedInstituteId || editData?.instituteId || null;
        }

        // Case 2 — Approve AI suggestion: institute comes from the ai_suggestions record
        if (isApproveMode) {
            return selectedInstituteId || approveData?.instituteId || null;
        }

        // Case 3 — New listing:
        //   Super Admin  → use the institute they selected from the dropdown
        //   Institute Admin / Alumni → use their assigned institute from session
        if (isSuperAdmin) {
            return selectedInstituteId || null;
        }

        return sessionState?.primaryInstitute || sessionState?.instituteId || null;
    }, [isEditMode, isApproveMode, isSuperAdmin, selectedInstituteId, editData, approveData, sessionState]);

    const loggedInAlumniUserId = useMemo(() => {
        const user = sessionStorage.getItem('user');
        const parsedUser = user ? JSON.parse(user) : null;
        return sessionState?.id || parsedUser?.id || state?.data?.id || state?.internal?.model?.id || null;
    }, [sessionState, state]);

    // Auto-open when editData or approveData is provided
    useEffect(() => {
        if (prefillSource && !show) {
            openModal(prefillSource);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editData, approveData]);

    const openModal = (prefillData) => {
        const prevFormConfig = state.formConfig;
        const prevData = state.data;

        // Build modal domain — start from global domains so all dropdowns
        // (BUSINESS_CATEGORY, BUSINESS_SUBCATEGORY, COUNTRY_CODE, CITY_CODE, etc.)
        // have the full original options, not page-specific overrides.
        const modalDomain = new Map([...globalState.domain]);
        // Preserve any user-specific overrides (top countries/cities) from current state
        if (state.domain) {
            // Only copy keys that don't conflict with the form's required dropdowns
            const overrideSafeKeys = ['INSTITUTE_DOMAIN'];
            overrideSafeKeys.forEach((key) => {
                if (state.domain.has(key)) modalDomain.set(key, state.domain.get(key));
            });
        }

        dispatch({
            type: 'DATA_INIT',
            payload: {
                formConfig: AddProviderPageConfig,
                data: {
                    ...state.data,
                    addProvider: {
                        categoryCode: prefillData?.categoryCode || '',
                        subcategoryCode: prefillData?.subcategoryCode || '',
                        businessName: prefillData?.businessName || '',
                        servicesDescription: prefillData?.servicesDescription || '',
                        country: prefillData?.country ? String(prefillData.country) : '',
                        city: prefillData?.city ? String(prefillData.city) : '',
                        experienceYears: prefillData?.experienceYears ? String(prefillData.experienceYears) : '',
                        pricing: prefillData?.pricing ? String(prefillData.pricing) : '',
                        websiteUrl: prefillData?.websiteUrl || '',
                        portfolioLink: prefillData?.portfolioLink || '',
                    },
                },
                domain: modalDomain,
                internal: state.internal,
                routeInfo: state.routeInfo,
            },
        });

        // Pre-fill non-FormBuilder fields
        if (prefillData) {
            if (prefillData.instituteId) {
                setSelectedInstituteId(String(prefillData.instituteId));
                const inst = instituteDomain.find((i) => String(i.code) === String(prefillData.instituteId));
                setInstituteSearchText(inst?.value || String(prefillData.instituteId));
            }
            setAvailabilityStatus(prefillData.availabilityStatus || '');
            if (prefillData.providerName) {
                setSearchText(prefillData.providerName);
                setSelectedAlumni({
                    uuid: prefillData.userId,
                    firstName: prefillData.providerName?.split(' ')[0] || '',
                    lastName: prefillData.providerName?.split(' ').slice(1).join(' ') || '',
                    programName: prefillData.programName || '',
                    passOutYear: prefillData.passOutYear || '',
                    profilePictureFilePath: prefillData.imagePath || '',
                });
            }
            // Edit mode pre-checks consent (already given previously)
            // Approve mode does NOT pre-check consent — admin must confirm
            setConsentConfirmed(!isApproveMode);
        }

        sessionStorage.setItem('prevFormConfig', JSON.stringify(prevFormConfig));
        sessionStorage.setItem('prevData', JSON.stringify(prevData, (key, value) => {
            if (value instanceof Map) return Object.fromEntries(value);
            return value;
        }));

        setShow(true);
    };

    const handleShow = () => {
        if (!allowPerformAction()) {
            displayActionNotAllowedAlert();
            return;
        }
        openModal(null);
    };

    const handleClose = () => {
        setShow(false);
        resetForm();

        try {
            const prevFormConfig = JSON.parse(sessionStorage.getItem('prevFormConfig') || 'null');
            const prevData = JSON.parse(sessionStorage.getItem('prevData') || 'null');

            if (prevFormConfig && prevData) {
                dispatch({
                    type: 'DATA_INIT',
                    payload: {
                        formConfig: prevFormConfig,
                        data: prevData,
                        domain: new Map([...state.domain]),
                        internal: state.internal,
                        routeInfo: state.routeInfo,
                    },
                });
            }

            sessionStorage.removeItem('prevFormConfig');
            sessionStorage.removeItem('prevData');
        } catch (e) {
            console.error('Error restoring previous state:', e);
        }

        if (onEditClose) onEditClose();
    };

    const resetForm = () => {
        setSelectedInstituteId('');
        setInstituteSearchText('');
        setShowInstituteDropdown(false);
        setSearchText('');
        setSearchResults([]);
        setSelectedAlumni(null);
        setAvailabilityStatus('');
        setConsentConfirmed(false);
        setAdditionalNotes('');
        setErrors({});
        setShowSearchDropdown(false);
    };

    // Alumni search with debounce
    const doSearch = useCallback(async (value) => {
        if (!value || value.length < 3) {
            setSearchResults([]);
            setShowSearchDropdown(false);
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        try {
            let instituteIdToPass;
            if (isSuperAdmin) {
                instituteIdToPass = selectedInstituteId;
            } else if (sessionState?.userTypeCode === 'INSTITUTE_ADMIN') {
                instituteIdToPass = resolvedInstituteId;
            }
            const response = await BusinessDirectoryService.searchAlumni(
                value,
                instituteIdToPass || undefined
            );
            const students = response.data?.students || [];
            setSearchResults(students);
            setShowSearchDropdown(students.length > 0);
        } catch (err) {
            console.error('Search API error:', err);
            setSearchResults([]);
            setShowSearchDropdown(false);
        } finally {
            setIsLoading(false);
        }
    }, [isSuperAdmin, selectedInstituteId, resolvedInstituteId, sessionState]);

    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchText(value);
        setSelectedAlumni(null);

        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => doSearch(value), 400);
    };

    const handleSelect = (alumni) => {
        setSelectedAlumni(alumni);
        setSearchText(`${alumni.firstName || ''} ${alumni.lastName || ''}`.trim());
        setSearchResults([]);
        setShowSearchDropdown(false);
        setErrors((prev) => ({ ...prev, searchText: '' }));
    };

    const isValidUrl = (str) => {
        if (!str) return true;
        const pattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/[\w\-.~:/?#[\]@!$&'()*+,;=%]*)?$/i;
        return pattern.test(str.trim());
    };

    // Validation
    const validate = () => {
        const temp = {};
        const formData = state.data?.addProvider || {};
        let hasFormBuilderError = false;

        // FormBuilder field errors — use setError so they show inline below each control
        if (!formData.categoryCode) {
            setError('addProvider.categoryCode', ['Service Category is required'], dispatch);
            hasFormBuilderError = true;
        } else {
            setError('addProvider.categoryCode', [], dispatch);
        }

        if (!formData.servicesDescription || formData.servicesDescription.trim().length < 10) {
            setError('addProvider.servicesDescription', ['Services description is required (min 10 characters)'], dispatch);
            hasFormBuilderError = true;
        } else {
            setError('addProvider.servicesDescription', [], dispatch);
        }

        if (!formData.country) {
            setError('addProvider.country', ['Country is required'], dispatch);
            hasFormBuilderError = true;
        } else {
            setError('addProvider.country', [], dispatch);
        }

        if (!formData.experienceYears) {
            setError('addProvider.experienceYears', ['Years of Experience is required'], dispatch);
            hasFormBuilderError = true;
        } else if (isNaN(Number(formData.experienceYears)) || Number(formData.experienceYears) < 0) {
            setError('addProvider.experienceYears', ['Please enter a valid number'], dispatch);
            hasFormBuilderError = true;
        } else {
            setError('addProvider.experienceYears', [], dispatch);
        }

        if (formData.pricing && String(formData.pricing).trim().length > 0 && isNaN(Number(formData.pricing))) {
            setError('addProvider.pricing', ['Tentative Pricing must be a valid number'], dispatch);
            hasFormBuilderError = true;
        } else {
            setError('addProvider.pricing', [], dispatch);
        }

        if (formData.websiteUrl && formData.websiteUrl.trim().length > 0 && !isValidUrl(formData.websiteUrl.trim())) {
            setError('addProvider.websiteUrl', ['Please enter a valid URL (e.g., https://example.com)'], dispatch);
            hasFormBuilderError = true;
        } else {
            setError('addProvider.websiteUrl', [], dispatch);
        }

        if (formData.portfolioLink && formData.portfolioLink.trim().length > 0 && !isValidUrl(formData.portfolioLink.trim())) {
            setError('addProvider.portfolioLink', ['Please enter a valid URL (e.g., https://portfolio.example.com)'], dispatch);
            hasFormBuilderError = true;
        } else {
            setError('addProvider.portfolioLink', [], dispatch);
        }

        if (hasFormBuilderError) {
            dispatch({ type: 'SHOW_ERRORS' });
        }

        // Custom field errors — local state
        if (isSuperAdmin && !selectedInstituteId && !isApproveMode && !isEditMode) temp.selectedInstituteId = 'Please select an institute first';
        if (!isAlumni && !selectedAlumni) temp.searchText = 'Please search and select an alumni';
        if (!availabilityStatus) temp.availabilityStatus = 'Availability Status is required';
        if (!consentConfirmed) temp.consentConfirmed = 'Consent confirmation is required';

        setErrors(temp);
        return Object.keys(temp).length === 0 && !hasFormBuilderError;
    };

    // Submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        const formData = state.data?.addProvider || {};

        setIsSubmitting(true);
        try {
            const payload = {
                userId: isAlumni ? loggedInAlumniUserId : selectedAlumni?.uuid || null,
                instituteId: resolvedInstituteId,
                businessName: (formData.businessName || '').trim() || null,
                categoryCode: formData.categoryCode,
                subcategoryCode: formData.subcategoryCode || null,
                servicesDescription: (formData.servicesDescription || '').trim(),
                experienceYears: Number(formData.experienceYears) || null,
                availabilityStatus,
                pricing: formData.pricing ? Number(formData.pricing) : null,
                websiteUrl: (formData.websiteUrl || '').trim() || null,
                portfolioLink: (formData.portfolioLink || '').trim() || null,
                country: formData.country ? Number(formData.country) : null,
                city: formData.city ? Number(formData.city) : null,
                status: 'ACTIVE',
            };

            if (isEditMode && editData?.id) {
                // Existing edit flow — update existing provider
                await BusinessDirectoryService.updateProvider(editData.id, payload);
                toast.success('Service provider updated successfully!');
            } else if (isApproveMode && approveData?.id) {
                // Approve AI suggestion: create provider, then mark suggestion as approved
                const createResp = await BusinessDirectoryService.createProvider(payload);
                try {
                    const AiSuggestedService = (await import('./AiSuggestedService')).default;
                    await AiSuggestedService.approveSuggestion(approveData.id, {
                        providerId: createResp?.data?.id,
                    });
                } catch (approveErr) {
                    console.warn('Created provider but failed to mark suggestion approved:', approveErr);
                }
                toast.success('Service provider added to directory and suggestion approved!');
            } else {
                await BusinessDirectoryService.createProvider(payload);
                toast.success('Service provider added to directory successfully!');
            }

            handleClose();

            // Refresh the listing after restore
            setTimeout(() => {
                const searchCriteria = JSON.parse(sessionStorage.getItem('prevData') || '{}')?.searchCriteria || {};
                BusinessDirectoryService.search(searchCriteria).then((response) => {
                    const data = response?.data ?? {};
                    setStateValue('providerList', data?.data ?? []);
                    setStateValue('pagination', {
                        total: data?.total ?? 0,
                        page: data?.page ?? 1,
                        limit: data?.limit ?? 20,
                    });
                });
            }, 300);
        } catch (err) {
            console.error('Submit API error:', err);
            toast.error(isEditMode ? 'Failed to update service provider.' : 'Failed to add service provider. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {/* Only show button trigger in add mode (not edit mode) */}
            {!isEditMode && (
                <Button 
                    className={`${props.className} ${getActionElementClass()}`} 
                    onClick={handleShow}>
                    <i className="icon-plus circle"></i>
                    <span>{props.modalTitle}</span>
                </Button>
            )}

            <Modal show={show} onHide={handleClose} size="lg max-600" centered>
                <Modal.Body className="p-4 p-md-5">
                    <h5 className="font-30 font-600 mb-4">{isEditMode ? 'Edit Service Provider' : 'Add Service Provider'}</h5>

                    <a
                        href="javascript:;"
                        className="position-absolute top-20 right-20 m-0 text-secondary font-30"
                        onClick={handleClose}
                        aria-label="Close">
                        <i className="bi bi-x-circle-fill"></i>
                    </a>

                    <form className="needs-validation" noValidate onSubmit={handleSubmit}>
                        <div className="row g-4">
                            {/* Institute selector — only for SUPER_ADMIN */}
                            {isSuperAdmin && (
                                <div className="col-md-12 position-relative">
                                    <label className="form-label">Select Institute*</label>
                                    <input
                                        type="text"
                                        className={`form-control form-search ${errors.selectedInstituteId ? 'is-invalid' : ''}`}
                                        placeholder="Search institute by name..."
                                        value={instituteSearchText}
                                        onChange={(e) => {
                                            setInstituteSearchText(e.target.value);
                                            setShowInstituteDropdown(true);
                                            if (selectedInstituteId) {
                                                setSelectedInstituteId('');
                                                setSelectedAlumni(null);
                                                setSearchText('');
                                                setSearchResults([]);
                                            }
                                        }}
                                        autoComplete="off"
                                        disabled={isEditMode || isApproveMode}
                                    />
                                    {errors.selectedInstituteId && <div className="invalid-feedback">{errors.selectedInstituteId}</div>}

                                    {showInstituteDropdown && filteredInstitutes.length > 0 && (
                                        <div
                                            className="position-absolute w-100 bg-white border rounded mt-1 overflow-auto"
                                            style={{ zIndex: 1050, maxHeight: '200px' }}>
                                            {filteredInstitutes.map((item, idx) => (
                                                <div
                                                    key={item.code || idx}
                                                    className="p-2 border-bottom font-14"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => {
                                                        setSelectedInstituteId(String(item.code));
                                                        setInstituteSearchText(item.value);
                                                        setShowInstituteDropdown(false);
                                                        setSelectedAlumni(null);
                                                        setSearchText('');
                                                        setSearchResults([]);
                                                        setErrors((prev) => ({ ...prev, selectedInstituteId: '' }));
                                                    }}>
                                                    {item.value}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {selectedInstituteId && (
                                        <div className="d-flex align-items-center bg-gray-f5 rounded p-2 mt-2">
                                            <div className="flex-1">
                                                <span className="font-14 font-500">
                                                    {instituteDomain.find((i) => String(i.code) === selectedInstituteId)?.value || selectedInstituteId}
                                                </span>
                                            </div>
                                            {!isEditMode && (
                                                <a
                                                    href="javascript:;"
                                                    className="text-secondary ms-2"
                                                    onClick={() => {
                                                        setSelectedInstituteId('');
                                                        setInstituteSearchText('');
                                                        setSelectedAlumni(null);
                                                        setSearchText('');
                                                        setSearchResults([]);
                                                    }}>
                                                    <i className="bi bi-x-circle"></i>
                                                </a>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Search & Link Alumni - custom control */}
                            {!isAlumni && (
                                <div className="col-md-12 position-relative">
                                    <label className="form-label">Search & Link Alumni Profile</label>
                                    <input
                                        type="text"
                                        className={`form-control form-search ${errors.searchText ? 'is-invalid' : ''}`}
                                        placeholder="Search by Name..."
                                        value={searchText}
                                        onChange={handleInputChange}
                                        autoComplete="off"
                                        disabled={isEditMode || isApproveMode || (isSuperAdmin && !selectedInstituteId)}
                                    />
                                    {isSuperAdmin && !selectedInstituteId && !isEditMode && (
                                        <small className="text-muted">Please select an institute first to search alumni</small>
                                    )}
                                    {errors.searchText && <div className="invalid-feedback">{errors.searchText}</div>}

                                    {isLoading && (
                                        <div className="position-absolute w-100 bg-white border rounded p-2 mt-1" style={{ zIndex: 1050 }}>
                                            <span className="text-muted font-14">Searching...</span>
                                        </div>
                                    )}

                                    {showSearchDropdown && searchResults.length > 0 && (
                                        <div
                                            className="position-absolute w-100 bg-white border rounded mt-1 overflow-auto"
                                            style={{ zIndex: 1050, maxHeight: '240px' }}>
                                            {searchResults.map((alumni, idx) => (
                                                <div
                                                    key={alumni.uuid || idx}
                                                    className="d-flex align-items-center p-2 cursor-pointer border-bottom"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleSelect(alumni)}>
                                                    <img
                                                        src={getStudentImage(alumni)}
                                                        alt=""
                                                        className="rounded-circle me-2"
                                                        style={{ height: '36px', width: '36px', objectFit: 'cover' }}
                                                    />
                                                    <div>
                                                        <span className="font-14 font-500 d-block">
                                                            {`${alumni.firstName || ''} ${alumni.lastName || ''}`.trim()}
                                                        </span>
                                                        <span className="font-12 text-muted">
                                                            {[alumni.programName, alumni.passOutYear].filter(Boolean).join(', ')}
                                                            {alumni.registeredEmailId ? ` | ${alumni.registeredEmailId}` : ''}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {selectedAlumni && (
                                        <div className="d-flex align-items-center bg-gray-f5 rounded p-2 mt-2">
                                            <img
                                                src={getStudentImage(selectedAlumni)}
                                                alt=""
                                                className="rounded-circle me-2"
                                                style={{ height: '40px', width: '40px', objectFit: 'cover' }}
                                            />
                                            <div className="flex-1">
                                                <span className="font-14 font-500">
                                                    {`${selectedAlumni.firstName || ''} ${selectedAlumni.lastName || ''}`.trim()}
                                                </span>
                                                <span className="font-12 text-muted d-block">
                                                    {[selectedAlumni.programName, selectedAlumni.passOutYear].filter(Boolean).join(', ')}
                                                </span>
                                            </div>
                                            {!isEditMode && (
                                                <a
                                                    href="javascript:;"
                                                    className="text-secondary ms-2"
                                                    onClick={() => {
                                                        setSelectedAlumni(null);
                                                        setSearchText('');
                                                    }}>
                                                    <i className="bi bi-x-circle"></i>
                                                </a>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* FormBuilder-driven fields */}
                            <FormBuilder key="addProviderForm" section={AddProviderPageConfig.sectionRepository[0]} dataKey="addProvider" />

                            {/* Availability Status - custom radio */}
                            <div className="col-md-12">
                                <div className="d-flex flex-wrap justify-content-between align-items-center">
                                    <label className="form-label m-0 mb-1 font-16 font-500">Availability Status*</label>
                                </div>
                                {errors.availabilityStatus && (
                                    <div className="text-danger font-12 mb-1">{errors.availabilityStatus}</div>
                                )}
                                <div className="row g-3">
                                    <div className="col-auto">
                                        <div className="form-check form-check-inline">
                                            <input
                                                className="form-check-input modal-custom-radio ms-0"
                                                type="radio"
                                                name="AvailabilityStatus"
                                                id="Available"
                                                value="AVAILABLE"
                                                checked={availabilityStatus === 'AVAILABLE'}
                                                onChange={(e) => {
                                                    setAvailabilityStatus(e.target.value);
                                                    setErrors((prev) => ({ ...prev, availabilityStatus: '' }));
                                                }}
                                            />
                                            <label className="form-check-label" htmlFor="Available">
                                                Available
                                            </label>
                                        </div>
                                    </div>
                                    <div className="col-auto">
                                        <div className="form-check form-check-inline">
                                            <input
                                                className="form-check-input modal-custom-radio ms-0"
                                                type="radio"
                                                name="AvailabilityStatus"
                                                id="Busy"
                                                value="BUSY"
                                                checked={availabilityStatus === 'BUSY'}
                                                onChange={(e) => {
                                                    setAvailabilityStatus(e.target.value);
                                                    setErrors((prev) => ({ ...prev, availabilityStatus: '' }));
                                                }}
                                            />
                                            <label className="form-check-label" htmlFor="Busy">
                                                Busy
                                            </label>
                                        </div>
                                    </div>
                                    <div className="col-auto">
                                        <div className="form-check form-check-inline">
                                            <input
                                                className="form-check-input modal-custom-radio ms-0"
                                                type="radio"
                                                name="AvailabilityStatus"
                                                id="NotAvailable"
                                                value="NOT_AVAILABLE"
                                                checked={availabilityStatus === 'NOT_AVAILABLE'}
                                                onChange={(e) => {
                                                    setAvailabilityStatus(e.target.value);
                                                    setErrors((prev) => ({ ...prev, availabilityStatus: '' }));
                                                }}
                                            />
                                            <label className="form-check-label" htmlFor="NotAvailable">
                                                Not Available
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Consent Confirmation */}
                            <div className="col-12">
                                <div className={`bg-gray-f5 rounded p-4 ${errors.consentConfirmed ? 'border border-danger' : ''}`}>
                                    <div className="form-check form-check-inline align-items-start">
                                        <input
                                            className="form-check-input ms-0 mt-1 me-2"
                                            type="checkbox"
                                            id="Consent"
                                            checked={consentConfirmed}
                                            onChange={(e) => {
                                                setConsentConfirmed(e.target.checked);
                                                setErrors((prev) => ({ ...prev, consentConfirmed: '' }));
                                            }}
                                        />
                                        <label
                                            className="form-check-label d-flex flex-column align-items-start lh-base"
                                            htmlFor="Consent">
                                            <span className="font-500 d-block text-start">Consent Confirmation*</span>
                                            I confirm that the alumni has given consent to be listed in the Business Directory
                                            and agrees to be contacted by the institute for service inquiries.
                                        </label>
                                    </div>
                                    {errors.consentConfirmed && (
                                        <div className="text-danger font-12 mt-1">{errors.consentConfirmed}</div>
                                    )}
                                </div>
                            </div>

                            {/* Additional Notes for supper admin only */}
                            {isSuperAdmin && (
                            <div className="col-md-12">
                                <label className="form-label m-0 mb-1 font-16 font-500">Additional Notes/Comments</label>
                                <textarea
                                    className="form-control h-auto"
                                    rows="3"
                                    placeholder="Any additional information..."
                                    value={additionalNotes}
                                    onChange={(e) => setAdditionalNotes(e.target.value)}
                                />
                            </div>
                            )}

                            {/* Actions */}
                            <div className="col-12">
                                <div className="row g-2 justify-content-center align-items-center">
                                    <div className="col-auto">
                                        <button type="button" className="btn btn-border-gray height-40" onClick={handleClose}>
                                            <span>Cancel</span>
                                        </button>
                                    </div>
                                    <div className="col-auto">
                                        <button
                                            type="submit"
                                            className="btn btn-green max-135"
                                            disabled={isSubmitting}>
                                            {isSubmitting ? (
                                                <>
                                                    <span
                                                        className="spinner-border spinner-border-sm me-2"
                                                        role="status"
                                                        aria-hidden="true"></span>
                                                    {isEditMode ? 'Saving...' : 'Adding...'}
                                                </>
                                            ) : (
                                                <span>{isEditMode ? 'Save Changes' : 'Add to Directory'}</span>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>
        </>
    );
}

export default AddServiceProviderModal;
